/**
 * @cogo/react-renderer/adapters/commerce-aliases — commerce-semantic type gap-closer.
 *
 * Maps the 12 commerce-semantic element types Phase 485 page synthesis emits
 * onto the generic concrete renderers shipped by `registerAllRenderers`.
 *
 * These 12 types are valid `element-types.json` registry members — they pass
 * the Phase 485 safety contract's semantic stage — but `registerAllRenderers`
 * registers NO renderer for them, so an unaliased element of one of these
 * types hits `ElementRenderer`'s `if (!Renderer)` branch and renders the
 * `data-unknown-type` placeholder. `registerCommerceAliases` closes that gap.
 *
 * `cta-button` / `add-to-cart-button` MUST be aliased here — they cannot be
 * trimmed to the generic `button` type in Phase 485 because the safety
 * contract's `contract-unbound-interactive` rule flags a `button` element
 * without an `events.bindings` entry, and a UI-tree-only golden has no flow
 * (RESEARCH.md §Element-type coverage audit Lever A warning, Assumption A4).
 *
 * Public exports: `registerCommerceAliases()`.
 *
 * Phase 486 — v77 demo design wire.
 */

import type { ElementRegistry } from "../core/element-registry"
import { ContainerRenderer, TextRenderer, ButtonRenderer } from "../renderers"

/**
 * Register the 12 Phase 485 commerce-semantic element types onto `reg`,
 * each mapped to the closest generic renderer:
 *
 * - Text-bearing semantic types → `TextRenderer` (reads `element.text` +
 *   `className` + `weight`).
 * - Section / layout semantic types → `ContainerRenderer` (walks `children`
 *   + applies `className`).
 * - CTA semantic types → `ButtonRenderer` (reads `text` / `variant` / `size`).
 *
 * Mirrors the established HTML-semantic-alias block in `registerAllRenderers`
 * (`header`/`footer`/`nav`/… → `ContainerRenderer`, `heading` → `TextRenderer`).
 */
export function registerCommerceAliases(reg: ElementRegistry): void {
  // Text-bearing semantic types → TextRenderer
  reg.register("headline", TextRenderer)
  reg.register("subheadline", TextRenderer)
  reg.register("brand-logo", TextRenderer)
  // Section / layout semantic types → ContainerRenderer
  reg.register("hero-banner", ContainerRenderer)
  reg.register("product-card", ContainerRenderer)
  reg.register("image-gallery", ContainerRenderer)
  reg.register("price-display", ContainerRenderer)
  reg.register("order-review", ContainerRenderer)
  reg.register("step-indicator", ContainerRenderer)
  reg.register("confirmation-display", ContainerRenderer)
  // CTA semantic types → ButtonRenderer
  reg.register("cta-button", ButtonRenderer)
  reg.register("add-to-cart-button", ButtonRenderer)
}
